import { ChangeDetectionStrategy, Component, HostBinding, inject, signal } from '@angular/core';

import { UiGridComponent } from '@uigy/shared/components/ui-grid/components/ui-grid/ui-grid.component';
import { LanguageService } from '@uigy/shared/services/language.service';
import { VulnerabilityFindingsGridSettingsService } from '@uigy/modules/findings/components/findings-vulnerabilities/services/vulnerability-findings-grid-settings.service';
import { VulnerabilityFindingsService } from '@uigy/modules/findings/components/findings-vulnerabilities/services/vulnerability-findings.service';
import { NavigationRoutesService } from '@uigy/core/modules/layout/navigation-routes.service';
import { VulnerabilityFindingsSideDrawerService } from '@uigy/modules/findings/components/findings-side-panel/services/vulnerability-findings-side-drawer.service';
import {
  Capability, FindingField,
  FindingFragment,
  GetFindingsGroupedQuery,
  GetFindingsGroupedQueryVariables,
  GetFindingsQuery,
  GetFindingsQueryVariables,
} from '@uigy/generated/graphql';
import { UiGridOptions } from '@uigy/shared/components/ui-grid/models/ui-grid';
import { UiGridIdsEnum } from '@uigy/shared/components/ui-grid/enums/ui-grid-ids.enum';
import { RoutesStatesNamesEnum } from '@uigy/shared/enums/routesStatesNames.enum';
import { InventoryTabsComponent } from '@uigy/modules/inventory/components/inventory-tabs/inventory-tabs.component';
import { GridFormatterService } from '@uigy/shared/modules/grid/formatters/grid-formatter.service';
import { IconsEnum } from '@uigy/generated/icons.enum';
import { HOST_ATTR_APP_ID } from '@uigy/shared/directives/app-id/host-attributes.constant';
import { AppIdsEnum } from '@uigy/shared/directives/app-id/appIds.enum';
import { ActionButtonVariant } from '@uigy/shared/components/ui-buttons-action-bar/models';
import { drawerDefaultWidth } from '@uigy/shared/components/ui-drawer/constants';
import { SaveFiltersFieldNames } from '@uigy/shared/components/ui-grid/components/filters/saved-filters/save-filter-form/interfaces';
import { CopyTextRendererComponent } from '@uigy/shared/modules/grid/cell-renderers/copy-text-renderer/copy-text-renderer.component';

@Component({
  selector: 'app-findings-vulnerabilities',
  imports: [
    UiGridComponent,
  ],
  templateUrl: './findings-vulnerabilities.component.html',
  styleUrl: './findings-vulnerabilities.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FindingsVulnerabilitiesComponent {

  private languageService = inject(LanguageService);
  private findingsGridSettingsService = inject(VulnerabilityFindingsGridSettingsService);
  private findingsService = inject(VulnerabilityFindingsService);
  private navigationRoutesService = inject(NavigationRoutesService);
  public findingsSideDrawerService = inject(VulnerabilityFindingsSideDrawerService);
  protected readonly GridFormatterService = GridFormatterService;
  protected readonly ActionButtonVariant = ActionButtonVariant;
  protected readonly IconsEnum = IconsEnum;

  private getAllFindings$ = (pagingParams: GetFindingsQueryVariables) => this.findingsService.getFindings(pagingParams);
  private getAllFindingsGrouping$ = (pagingParams: GetFindingsGroupedQueryVariables) => this.findingsService.getFindingsGrouped(pagingParams);

  public readonly findingsTexts = this.languageService.texts.findings;

  @HostBinding(HOST_ATTR_APP_ID)
  public readonly hostAppId: string = AppIdsEnum.page_findings;

  public gridOptions: UiGridOptions<FindingFragment, GetFindingsQuery, GetFindingsGroupedQuery> = {
    gridId: UiGridIdsEnum.FINDINGS,
    gridTitle: this.findingsTexts.vulnerability_findings.grid_title,
    exportFileName: this.findingsTexts.title,
    dataSource: this.getAllFindings$,
    groupingDataSource: this.getAllFindingsGrouping$,
    transformFilterModel: this.findingsGridSettingsService.findingsTransformFilterModel,
    columnDefs: this.findingsGridSettingsService.findingsColumnDefs,
    rowSelection: 'single',
    suppressDefaultSelection: true,
    activateUrlParams: true,
    noRowsOverlayComponentParams: {
      title: this.findingsTexts.vulnerability_findings.no_results.title,
      items: [
        {
          text: this.findingsTexts.vulnerability_findings.no_results.description,
          actionButton: {
            text: this.findingsTexts.vulnerability_findings.no_results.button,
            buttonType: 'link',
            capability: Capability.ReadAssets,
            action: () => this.navigationRoutesService.navigateWithParams([RoutesStatesNamesEnum.inventory], InventoryTabsComponent.defaultNavigation),
          },
        },
      ],
    },
    headerOptions: {
      hideSelectedRowsCounter: true,
      viewportOffset: drawerDefaultWidth(),
      renderSaveFilterButton: true,
      renderRestoreFiltersButton: true,
      savedFiltersFieldName: SaveFiltersFieldNames.FindingField
    },
    components: {
      copyTextRendererComponent: CopyTextRendererComponent,
    },
  };

  public focusedRowChanged(id: string|undefined): void {
      this.findingsSideDrawerService.setSelectedFinding(id);
  }
}
